import { Trans } from '@lingui/macro'

import { SCREEN_CONFIG } from '@config/screenConfig'
import { useCompanyStylesCtx } from '@contexts/CompanyStylesContext/CompanyStylesProvider'
import { useLayoutIsXS } from '@segments/Responsive/client'
import { BackToMenuButton, useWaiterRouter } from '@segments/Routing/client'
import { BaseHeaderContent, HeaderScreen, HeroScreen } from '@segments/ScreenLayout'
import * as React from 'react'

import { InfoCard } from '~shared/components/InfoCard/InfoCard'
import {
  Agreements,
  FormSubmitButtonDesktop,
  FormSubmitButtonMobile,
  PromoCodes,
} from '~shared/segments/FormScreen'
import { PaymentMethod } from '~shared/segments/PaymentMethods'
import { InputGroup } from '~ui/components/formElements/accessories/InputGroup'
import { Box, Grid } from '~ui/components/layout/Box'
import { Stack } from '~ui/components/layout/Stack'
import { H2 } from '~ui/components/typography/Heading/Heading'
import { getGridPlacement } from '~ui/utils/gridUtils'

import { ContactInputGroup } from './ContactInputGroup'
import { OrderNoteInput } from './OrderNoteInput'

type TProps = NoChildren

const CONTENT_MOBILE_PADDING_HORIZONTAL = SCREEN_CONFIG.contentPaddingHorizontalMobile
const CONTENT_MOBILE_PADDING_VERTICAL = `medium`
const NAVIGATE_TO = `menu`

/**
 * Handles mostly the responsive questions on the FormScreen (logic is in the context providers).
 */
export const FormScreenResponsiveContent: React.FC<TProps> = () => {
  const router = useWaiterRouter()

  const isMobile = useLayoutIsXS()
  const companyStyles = useCompanyStylesCtx()

  if (isMobile) {
    return (
      <HeaderScreen
        stickyHeader
        headerContent={
          <BaseHeaderContent title={companyStyles.primaryNameLabel} navigateTo={NAVIGATE_TO} />
        }
      >
        <Grid
          padding={{
            horizontal: CONTENT_MOBILE_PADDING_HORIZONTAL,
            vertical: CONTENT_MOBILE_PADDING_VERTICAL,
          }}
          rowGap={`extraLarge`}
          width={`100%`}
          maxWidth={SCREEN_CONFIG.maxFormWidth}
          margin={{ vertical: `none`, horizontal: `auto` }}
        >
          <ContactInputGroup />
          <OrderNoteInput />

          <PromoCodes refetchQueries={[`QWaiterCart`]} />
          <PaymentMethod />

          {/* TODO - what about age control on the phone?
           especially in waiter it makes sense */}
          <Agreements />

          <Grid alignItems={`flex-end`} rowGap={`smallMedium`}>
            <FormSubmitButtonMobile onBackClick={() => router.push(`menu`)} />
          </Grid>
        </Grid>
      </HeaderScreen>
    )
  }

  return (
    <HeroScreen navigateTo={NAVIGATE_TO}>
      <Grid
        rowGap={`extraLarge`}
        margin={{ bottom: `extraLarge`, top: `none`, left: `none`, right: `none` }}
      >
        {/* This empty InfoCard is here to create the correct gap between the top image and the content.
          As in future there will probably be some info like in a shop, adding props to heroScreen, etc. is not worth it. */}
        <InfoCard opacity={0} isMobileOrTablet>
          <Box />
        </InfoCard>

        <Grid
          rowGap={`largePlus`}
          columnGap={`extraLarge`}
          alignItems={`flex-start`}
          margin={{ horizontal: `auto`, vertical: `none` }}
          maxWidth={SCREEN_CONFIG.maxFormWidth}
        >
          <Grid rowGap={`extraLarge`} {...getGridPlacement({ col: `all` })}>
            <H2 fontStyle={`regular`} fontSize={`l28`} contrast={`low`}>
              <Trans>Fill the form, please</Trans>
            </H2>
          </Grid>

          <Stack direction={`column`} spacing={`extraLarge`}>
            <ContactInputGroup />
            <InputGroup>
              <OrderNoteInput />

              <PromoCodes />
            </InputGroup>

            <PaymentMethod />

            <Agreements />
          </Stack>

          <Grid
            rowGap={`smallMedium`}
            justifyContent={`center`}
            margin={{ top: `extraLarge`, bottom: `none`, left: `none`, right: `none` }}
          >
            <FormSubmitButtonDesktop />
            <BackToMenuButton />
          </Grid>
        </Grid>
      </Grid>
    </HeroScreen>
  )
}
